/** Shared presentational pieces. */
import { useEffect, useState, type ReactNode } from "react";

export function Dot({ state }: { state: "live" | "idle" | "warn" | "deny" }) {
  return <span className={`dot${state === "idle" ? "" : ` dot--${state}`}`} />;
}

export function Presence({ online, since }: { online: boolean; since: string }) {
  return (
    <span className={`presence${online ? " presence--live" : ""}`}>
      <Dot state={online ? "live" : "idle"} />
      <span className="presence__text">{online ? "Reachable" : since}</span>
    </span>
  );
}

export function Notice({
  kind = "error",
  children,
}: {
  kind?: "error" | "warn" | "ok";
  children: ReactNode;
}) {
  if (!children) return null;
  return <div className={`notice notice--${kind}`}>{children}</div>;
}

export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {hint ? <p style={{ margin: "0 auto 1rem" }}>{hint}</p> : null}
      {action}
    </div>
  );
}

export function Drawer({
  title,
  onClose,
  children,
  footer,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true">
        <div className="drawer__head">
          <h2 style={{ minWidth: 0 }} className="truncate">{title}</h2>
          <button className="btn btn--ghost btn--sm" style={{ marginLeft: "auto" }} onClick={onClose}>
            Close
          </button>
        </div>
        <div className="drawer__body">{children}</div>
        {footer ? <div className="drawer__foot">{footer}</div> : null}
      </aside>
    </>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: T; label: string }>;
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={active === t.id}
          className="tab"
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Small async wrapper so pages do not each reinvent loading/error handling. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fn()
      .then((v) => {
        if (alive) {
          setData(v);
          setError("");
        }
      })
      .catch((e) => {
        if (alive) setError(e?.message ?? String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1), setData };
}

export function Confirm({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  danger,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}) {
  return (
    <Drawer
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button className={`btn ${danger ? "btn--danger" : "btn--primary"}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
          <button className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        </>
      }
    >
      {body}
    </Drawer>
  );
}
